// @flow
import buildAmenityItem from './tripAmenity'
import { config } from '../../../config'

// Types
import type { PriceUpdate } from '../../../models/price'
import type { PrebookPriceUpdate } from '../../../api_types'

/**
 * Build an object with updated price information
 * @param  {Object} price  Price object form the API
 * @return {PriceUpdate}   Normalized price update object
 * @private
 */
function buildPrebookPriceUpdate(price: PrebookPriceUpdate): PriceUpdate {
  const amenities = price.amenities || []
  const finalPriceRaw = parseFloat(price.final_price.value)
  const gratuityRaw = parseFloat(price.gratuity.value)
  const chargablePrice = price.final_chargeable_price || {}

  const priceDiff = !price.price_difference
    ? {
        diffPrice: '',
        diffPriceRaw: 0,
        diffPriceStripe: 0,
      }
    : {
        diffPrice: price.price_difference.display,
        diffPriceRaw: parseFloat(price.price_difference.value),
        diffPriceStripe: parseInt(
          (price.price_difference: any).smallest_currency_unit || 0,
          10
        ),
      }

  const reservationPrice = !price.reservation_original_price
    ? {
        reservationPrice: '',
        reservationPriceRaw: 0,
      }
    : {
        reservationPrice: price.reservation_original_price.display,
        reservationPriceRaw: parseFloat(price.reservation_original_price.value),
      }

  return {
    ...priceDiff,
    ...reservationPrice,
    gratuityRaw,
    finalPriceRaw,
    currency: price.currency,
    rewardPoints: price.loyalty?.points || 0,
    amenities: amenities.map(buildAmenityItem),
    couponCode: price.coupon_code || '',
    gratuity: price.gratuity.display,
    bookingFee: price.booking_fee.display,
    bookingFeeValue: parseFloat(price.booking_fee.value),
    bookingFeeOriginal: price.booking_fee_original.display,
    couponDiscount: price.coupon_discount.display,
    couponDiscountRaw: parseFloat(price.coupon_discount.value),
    pricePerVehicle: price.price_per_vehicle.display,
    pricePerVehicleRaw: parseFloat(price.price_per_vehicle.value),
    originalPrice: price.original_price.display,
    originalPriceRaw: parseFloat(price.original_price.value),
    finalPrice: price.final_price.display,
    finalPriceCompact: config.NO_COMPACT_PRICE
      ? price.final_price.display
      : price.final_price.compact,
    finalPriceUsd: parseFloat(price.final_price_usd.value),
    finalChargeStripePrice: parseFloat(
      chargablePrice.smallest_currency_unit || '0'
    ),
    finalChargeCurrency: chargablePrice.currency || 'USD',
    finalChargePrice: chargablePrice.display || '$0',
    finalPriceUsdStripe: parseFloat(
      price.final_price_usd.smallest_currency_unit
    ),
    finalPriceStripe: parseInt(price.final_price.smallest_currency_unit, 10),
    forceThreeDSecure: !!price.force_3d_secure,
    postPayment: !!price.post_payment,
    canUseReservationCard: !!price.can_use_reservation_card,
    requirePaymentDetails: price.require_payment_details ?? true,
    meetAndGreetPrice: '',
    gratuityPercents: parseInt(price.gratuity_percent || '0', 10),
    disclaimer: price.disclaimer || '',
    returnDisclaimer: price.return_disclaimer || '',
    platformFee: (price.platform_fee && price.platform_fee.display) || '',
    priceWithoutPlatformFee:
      (price.final_price_without_platform_fee &&
        price.final_price_without_platform_fee.display) ||
      '',
    supports: {
      creditCard: !!price.supports?.credit_card, // eslint-disable-line
      directBill: !!price.supports?.direct_bill, // eslint-disable-line
    },
  }
}

export default buildPrebookPriceUpdate
